"use client";

import {
    useQuery_experimental,
    type OptionalRestArgsOrSkip,
} from "convex/react";
import type { FunctionReference } from "convex/server";

type QueryRef = FunctionReference<"query">;

/**
 * Like `useQuery`, but never throws into React when the Convex UDF fails
 * (missing table, undeployed function, server error). Returns `undefined`
 * while loading or on error so optional features can degrade quietly.
 *
 * Prefer the normal `useQuery` for required data — this is for best-effort
 * subscriptions such as SDO HMI where a failure must not blank the page.
 */
export function useSafeQuery<Query extends QueryRef>(
    query: Query,
    ...args: OptionalRestArgsOrSkip<Query>
): Query["_returnType"] | undefined {
    const skip = args[0] === "skip";
    const result = useQuery_experimental({
        query,
        args: skip ? ("skip" as const) : ((args[0] ?? {}) as Exclude<
            OptionalRestArgsOrSkip<Query>[0],
            "skip"
        >),
        throwOnError: false,
    });

    if (result.status === "success") {
        return result.data;
    }
    return undefined;
}
